import i18n from 'shared/i18n/config';

// Map of language codes for consistency
const LANGUAGE_CODES = {
  en: 'en',
  da: 'da',
  dk: 'da',  // Normalize dk -> da
  es: 'es',
  no: 'no',
  sv: 'sv'
};

// Map for Open Graph locale codes
const OG_LOCALE_CODES = {
  en: 'en_US',
  da: 'da_DK',
  es: 'es_ES',
  no: 'no_NO',
  sv: 'sv_SE'
};

/**
 * Updates the document meta tags based on the current language
 * @param {object} options - Configuration options
 * @param {string} options.titleKey - i18n key for the title
 * @param {string} options.descriptionKey - i18n key for the description
 * @param {object} options.titleValues - Values to interpolate in the title
 * @param {object} options.descriptionValues - Values to interpolate in the description
 */
export const updateMetaTags = (options = {}) => {
  const {
    titleKey = 'meta.defaultTitle',
    descriptionKey = 'meta.defaultDescription',
    titleValues = {},
    descriptionValues = {}
  } = options;

  // Get current language and normalize it
  let currentLang = i18n.language;
  currentLang = LANGUAGE_CODES[currentLang] || currentLang;
  
  // Set document title
  const title = i18n.t(titleKey, titleValues);
  document.title = title;
  
  // Update meta description
  const description = i18n.t(descriptionKey, descriptionValues);
  
  // Update primary meta description
  const metaDescription = document.querySelector('meta[name="description"]:not([lang])');
  if (metaDescription) {
    metaDescription.setAttribute('content', description);
  }
  
  // Update language-specific meta description if exists
  const langMetaDescription = document.querySelector(`meta[name="description"][lang="${currentLang}"]`);
  if (langMetaDescription) {
    // This will be the prioritized description for search engines that understand lang attribute
    langMetaDescription.setAttribute('content', description);
  }
  
  // Update Open Graph tags
  const ogTitle = document.querySelector('meta[property="og:title"]');
  const ogDescription = document.querySelector('meta[property="og:description"]');
  const ogLocale = document.querySelector('meta[property="og:locale"]');
  
  if (ogTitle) {
    ogTitle.setAttribute('content', title);
  }
  
  if (ogDescription) {
    ogDescription.setAttribute('content', description);
  }
  
  if (ogLocale && OG_LOCALE_CODES[currentLang]) {
    ogLocale.setAttribute('content', OG_LOCALE_CODES[currentLang]);
  }
  
  // Update Twitter tags
  const twitterTitle = document.querySelector('meta[name="twitter:title"]');
  const twitterDescription = document.querySelector('meta[name="twitter:description"]');
  
  if (twitterTitle) {
    twitterTitle.setAttribute('content', title);
  }
  
  if (twitterDescription) {
    twitterDescription.setAttribute('content', description);
  }
  
  // Update language attribute
  document.documentElement.setAttribute('lang', currentLang);
  
  // Update hreflang tags
  const hreflangTags = document.querySelectorAll('link[hreflang]');
  hreflangTags.forEach(tag => {
    const lang = tag.getAttribute('hreflang');
    // Reset all to alternate first
    tag.setAttribute('rel', 'alternate');
  });
  
  // Set the current language one as canonical
  const currentHreflangTag = document.querySelector(`link[hreflang="${currentLang}"]`);
  if (currentHreflangTag) {
    currentHreflangTag.setAttribute('rel', 'canonical');
  } else if (currentLang === 'da') {
    // Special case for Danish which might use 'da' code in the app but 'dk' in URLs
    const dkHreflangTag = document.querySelector('link[hreflang="da"]');
    if (dkHreflangTag) {
      dkHreflangTag.setAttribute('rel', 'canonical');
    }
  }
};

/**
 * Updates meta tags when navigation occurs
 * @param {string} path - Current path
 */
export const updateMetaTagsForRoute = (path) => {
  // Map routes to specific meta tag keys
  const routeMetaMap = {
    '/': {
      titleKey: 'meta.home.title',
      descriptionKey: 'meta.home.description'
    },
    '/listings': {
      titleKey: 'meta.listings.title',
      descriptionKey: 'meta.listings.description'
    },
    '/about': {
      titleKey: 'meta.about.title',
      descriptionKey: 'meta.about.description'
    },
    '/contact': {
      titleKey: 'meta.contact.title',
      descriptionKey: 'meta.contact.description'
    },
    '/regions': {
      titleKey: 'meta.regions.title',
      descriptionKey: 'meta.regions.description'
    }
  };
  
  // Find the matching route or use defaults
  const metaConfig = Object.keys(routeMetaMap).find(route => 
    path === route || path.startsWith(`${route}/`)
  );
  
  if (metaConfig) {
    updateMetaTags(routeMetaMap[metaConfig]);
  } else {
    // Use default meta tags
    updateMetaTags();
  }
}; 